import React, { useState, useEffect } from 'react'
import BuyBookButton from './BuyBookButton'
import { formatBookDescription } from '../utils/formatBookDescription'
import Modal from './Modal'

interface Book {
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  cover_url?: string
  first_publish_year?: number
  number_of_pages?: number
  description?: string
  language?: string
}

interface BookSearchPreviewProps {
  book: Book
  onClose: () => void
  onSelect: () => void
  userProvider?: string | null
  onProviderSelected?: (provider: string) => void
  showSelectButton?: boolean
  onEdit?: () => void
}

export default function BookSearchPreview({ book, onClose, onSelect, userProvider, onProviderSelected, showSelectButton = false, onEdit }: BookSearchPreviewProps) {
  const [selected, setSelected] = useState(false)

  const handleSelect = () => {
    setSelected(true)
    onSelect()
    // Auto-close after showing the success state
    setTimeout(() => {
      onClose()
    }, 1000)
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true} className="overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <h2 className="text-2xl font-bold text-gray-900">Book Preview</h2>
        </div>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Cover */}
            {book.cover_url ? (
              <img
                src={book.cover_url}
                alt={book.title}
                className="w-32 h-48 object-cover rounded-lg shadow-md flex-shrink-0"
              />
            ) : (
              <div className="w-32 h-48 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg shadow-md flex items-center justify-center flex-shrink-0">
                <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            )}

            {/* Book Info */}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{book.title}</h3>
              <p className="text-lg text-gray-600 mb-4">by {book.author}</p>

              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {book.language && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Language</p>
                    <p className="text-sm font-medium text-gray-900">{book.language}</p>
                  </div>
                )}
                {book.first_publish_year && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Published</p>
                    <p className="text-sm font-medium text-gray-900">{book.first_publish_year}</p>
                  </div>
                )}
                {book.number_of_pages && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Pages</p>
                    <p className="text-sm font-medium text-gray-900">{book.number_of_pages}</p>
                  </div>
                )}
                {book.isbn && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">ISBN</p>
                    <p className="text-sm font-medium text-gray-900">{book.isbn}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {book.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">About this book</h4>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {formatBookDescription(book.description, 600)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
            {/* Buy button - clickable without closing modal */}
            <BuyBookButton
              book={book}
              userProvider={userProvider}
              onProviderSelected={onProviderSelected}
              variant="primary"
              size="lg"
            />

            {showSelectButton && (
              <button
                onClick={handleSelect}
                disabled={selected}
                className={`px-6 py-3 font-semibold rounded-lg transition-all shadow-lg flex items-center gap-2 ${
                  selected
                    ? 'bg-green-600 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-green-500/30'
                }`}
              >
                {selected ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Book Selected!
                  </>
                ) : (
                  'Select This Book'
                )}
              </button>
            )}

            {onEdit && (
              <button
                onClick={onEdit}
                className="px-6 py-3 bg-white border-2 border-indigo-300 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition-all"
              >
                Edit
              </button>
            )}
          </div>
        </div>
    </Modal>
  )
}
